import { Component } from '@angular/core';
import { AnalyticService, NotifyService } from '@app/services';
import { ActivatedRoute, Router } from '@angular/router';
import {
    ApplicantIndividualTestMessage,
    BulkApplicantMessage,
} from '@app/modules/applicant-tracker/interfaces/bulk-applicant-message';
import { ApplicantMessageService } from '@app/modules/applicant-tracker/services';
import { JobFunnelMessageTemplateForm } from '@app/modules/forms/applicant-tracker/components';
import { Location } from '@angular/common';
import { CanDeactivateComponent } from '@app/interfaces';
import { JobFunnelMessageTemplate } from '@models/recruiting/job-funnel-message-template.model';
import { JobFunnel } from '@models/recruiting/job-funnel.model';
import { AnalyticEvents } from '@app/enums';

@Component({
    templateUrl: './job-funnel-message-template.template.html',
})
export class JobFunnelMessageTemplateStoreView implements CanDeactivateComponent {
    isLoading = false;
    jobFunnel: JobFunnel;
    isSaveBulkEmailModalOpen = false;
    isSaveAndSendBulkEmailModalOpen = false;
    isDeactivateModalOpen = false;
    canNavigateBack = true;
    previousPath: string;
    expandSaveDropdown = false;

    jobFunnelMessageTemplate: JobFunnelMessageTemplate;

    constructor(
        private route: ActivatedRoute,
        private applicantMessageService: ApplicantMessageService,
        private notifyService: NotifyService,
        private analyticService: AnalyticService,
        private location: Location,
        private router: Router
    ) {
        this.jobFunnel = this.route.snapshot.data.models.get('jobFunnel');
        this.jobFunnelMessageTemplate = new JobFunnelMessageTemplate({
            atsJobFunnelId: this.jobFunnel.id,
        });
    }

    canDeactivate(): boolean {
        if (this.jobFunnelMessageTemplate.body) {
            this.canNavigateBack = false;
            this.isDeactivateModalOpen = true;
            this.previousPath = this.previousPath ?? this.location.path();
        }

        return this.canNavigateBack;
    }

    allowNavigation(): void {
        this.canNavigateBack = true;
        this.jobFunnelMessageTemplate.body = '';
        this.router.navigateByUrl(this.previousPath);
    }

    openSaveBulkEmail(): void {
        this.isSaveBulkEmailModalOpen = true;
    }

    closeSaveBulkEmail(): void {
        this.isSaveBulkEmailModalOpen = false;
    }

    openSaveAndSendBulkEmail(): void {
        this.isSaveAndSendBulkEmailModalOpen = true;
    }

    closeSaveAndSendBulkEmail(): void {
        this.isSaveAndSendBulkEmailModalOpen = false;
    }

    openDeactivateModalOpen(): void {
        this.isDeactivateModalOpen = true;
    }

    closeDeactivateModalOpen(): void {
        this.isDeactivateModalOpen = false;
    }

    hasFunnelApplicants(jobFunnelMessageTemplateForm: JobFunnelMessageTemplateForm): boolean {
        // Check if a job funnel has any applicants
        return (
            jobFunnelMessageTemplateForm.jobPosting.jobFunnelsWithActiveApplicantsCount.find((jobFunnel: JobFunnel) => {
                return jobFunnel.id === this.jobFunnelMessageTemplate.atsJobFunnelId;
            })?.activeApplicantsCount > 0
        );
    }

    async onSaveAndSend(jobFunnelMessageTemplateForm: JobFunnelMessageTemplateForm): Promise<void> {
        jobFunnelMessageTemplateForm.submit();

        if (!jobFunnelMessageTemplateForm.valid) {
            this.closeSaveAndSendBulkEmail();
            return;
        }

        if (!this.hasFunnelApplicants(jobFunnelMessageTemplateForm)) {
            this.closeSaveBulkEmail();
            this.closeSaveAndSendBulkEmail();
            this.notifyService.error('applicant-tracker.selectJobFunnelHasNoApplicants');
            return;
        }

        this.isLoading = true;

        const bulkApplicantMessage: BulkApplicantMessage = {
            isBulk: true,
            atsJobFunnelId: this.jobFunnelMessageTemplate.atsJobFunnelId,
            replyTo: this.jobFunnelMessageTemplate.replyTo,
            subject: this.jobFunnelMessageTemplate.subject,
            body: this.jobFunnelMessageTemplate.body,
        };

        try {
            await this.jobFunnelMessageTemplate.save();
            await this.applicantMessageService.onBulkSendApplicantMessage(bulkApplicantMessage);

            this.notifyService.success({
                key: 'applicant-tracker.subjectEmailWasSentToApplicantsInFunnelName',
                params: { subject: this.jobFunnelMessageTemplate.subject ?? '', funnelName: this.jobFunnel.funnelStep },
            });
            this.notifyService.success({
                key: 'applicant-tracker.subjectWillBeAutomaticallySentToApplicantsInFunnelName',
                params: { subject: this.jobFunnelMessageTemplate.subject ?? '', funnelName: this.jobFunnel.funnelStep },
            });

            this.analyticService.trackEvent(
                AnalyticEvents.RecruitingJobPostingFunnelMeatballAddEmailAutomationSaveAndSendSave
            );

            this.jobFunnelMessageTemplate.body = '';
            this.canNavigateBack = true;
            this.previousPath ? this.router.navigateByUrl(this.previousPath) : this.location.back();
        } catch (err) {
            this.notifyService.error('applicant-tracker.severalEmailsFailedToSend');
        } finally {
            this.isLoading = false;
        }

        this.closeSaveAndSendBulkEmail();
    }

    async onSave(jobFunnelMessageTemplateForm: JobFunnelMessageTemplateForm): Promise<void> {
        jobFunnelMessageTemplateForm.submit();

        if (!jobFunnelMessageTemplateForm.valid) {
            this.closeSaveBulkEmail();
            return;
        }

        this.isLoading = true;

        try {
            await this.jobFunnelMessageTemplate.save();

            this.notifyService.success({
                key: 'applicant-tracker.subjectWillBeAutomaticallySentToApplicantsInFunnelName',
                params: { subject: this.jobFunnelMessageTemplate.subject ?? '', funnelName: this.jobFunnel.funnelStep },
            });

            this.analyticService.trackEvent(AnalyticEvents.RecruitingJobPostingFunnelMeatballAddEmailAutomationSave);

            this.jobFunnelMessageTemplate.body = '';
            this.canNavigateBack = true;
            this.previousPath ? this.router.navigateByUrl(this.previousPath) : this.location.back();
        } catch (err) {
            this.notifyService.error('applicant-tracker.failedToSaveATemplate');
        } finally {
            this.isLoading = false;
        }

        this.closeSaveBulkEmail();
    }

    async onSendTest(jobFunnelMessageTemplateForm: JobFunnelMessageTemplateForm): Promise<void> {
        jobFunnelMessageTemplateForm.submit();

        if (!jobFunnelMessageTemplateForm.valid) {
            return;
        }

        const jobFunnelMessageTemplateTestMessage: ApplicantIndividualTestMessage = {
            atsJobId: this.jobFunnel.jobPostingId,
            body: this.jobFunnelMessageTemplate.body,
            replyTo: this.jobFunnelMessageTemplate.replyTo,
            subject: this.jobFunnelMessageTemplate.subject,
        };

        this.isLoading = true;

        try {
            await this.applicantMessageService.onApplicantIndividualTestMessage(jobFunnelMessageTemplateTestMessage);
            this.notifyService.success({
                key: 'applicant-tracker.testEmailSentToReplyTo',
                params: { replyTo: this.jobFunnelMessageTemplate.replyTo },
            });

            this.analyticService.trackEvent(AnalyticEvents.RecruitingAllEmailsSendTest);

            this.isLoading = false;
        } catch (err) {
            this.notifyService.error({
                key: 'applicant-tracker.testEmailFailedToSendToReplyTo',
                params: { replyTo: this.jobFunnelMessageTemplate.replyTo },
            });
            this.isLoading = false;
        }
    }
}
